import { oc } from "@orpc/contract";
import { z } from "zod";
import { ShipAndDebitEntityConfigSchema } from "@erp/shared-types";

/**
 * One S&D supplier as the suppliers admin page lists it.
 *
 * "S&D supplier" is deliberately the SAME set the 849 engine can resolve: an
 * `ap_suppliers` row whose company has a `ship-and-debit` entity_module_config
 * (that row's existence IS the module being enabled — there is no separate flag).
 * Listing anything wider would show suppliers the engine cannot route an email to;
 * anything narrower would hide one that is configured but idle.
 */
export const sdSupplierSummarySchema = z.object({
  supplierId: z.uuid(),
  companyId: z.uuid(),
  /** Legal name on the company record. */
  name: z.string(),
  /** `short_name` when set — what the 849 UI shows elsewhere. */
  displayName: z.string(),
  /** Every AS400 vendor number for the supplier (ABB has four). */
  vendorNumbers: z.array(z.string()),
  /** Trading-partner names/aliases — what resolves a parser config to this supplier. */
  tradingPartnerNames: z.array(z.string()),
  sd844DeliveryMethod: z.enum(["edi", "email"]).nullable(),
  sd849ResponseMethod: z.enum(["edi", "email"]).nullable(),
  /** Counts, so the list can show at a glance what is configured. */
  sd844EmailCount: z.int().nonnegative(),
  sd849EmailCount: z.int().nonnegative(),
  emailMatchingEnabled: z.boolean(),
  investigationEnabled: z.boolean(),
  /** Whether a parser config row exists for this supplier, and its status. */
  parserConfigStatus: z.enum(["active", "shadow", "draft"]).nullable(),
  /**
   * The `supplier_name` the matched parser config is actually STORED under, which is
   * frequently neither the company name nor the display name — configs are keyed by
   * their own formal name (`HOFFMAN` for "Hoffman", `ERICO CANADA INC.` for "Erico")
   * and are matched via `sd_trading_partner_names`. Any UI that loads or saves a
   * parser config must use THIS key: guessing the display name shows an empty editor
   * and saves a second config row that inbound processing never reads.
   */
  parserConfigSupplierName: z.string().nullable(),
  /**
   * How many DISTINCT config keys match this supplier. >1 is an ambiguous state:
   * which config reads an inbound email is decided by each config's own detection
   * patterns, so the one shown here may not be the one controlling live parsing —
   * the UI must surface that rather than present the picked key as authoritative.
   * (Multiple rows under ONE key are the supported multi-variant case, not this.)
   */
  parserConfigKeyCount: z.int().nonnegative(),
  /**
   * True when the supplier answers by email and has a parser config but matching is
   * off. That combination silently produces 849s with ZERO lines on the workflow
   * path, because the toggle gates parsing and not just matching — the single most
   * common misconfiguration in this module, so the list surfaces it directly.
   */
  needsMatchingToggle: z.boolean(),
});

export const listSdSuppliersResponseSchema = z.object({
  suppliers: z.array(sdSupplierSummarySchema),
});

const listContract = oc
  .input(z.object({}))
  .output(listSdSuppliersResponseSchema)
  .route({
    method: "GET",
    summary: "List every supplier with the Ship & Debit module enabled",
  });

/** The full stored module config for one supplier, for the detail sections. */
export const getSdSupplierResponseSchema = z.object({
  supplier: sdSupplierSummarySchema,
  config: ShipAndDebitEntityConfigSchema,
});

const getContract = oc
  .input(z.object({ companyId: z.uuid() }))
  .output(getSdSupplierResponseSchema)
  .route({
    method: "GET",
    summary: "Get one S&D supplier's full module config",
  });

export const sdSuppliersContracts = {
  list: listContract,
  get: getContract,
} as const;

export type SdSupplierSummary = z.infer<typeof sdSupplierSummarySchema>;
export type ListSdSuppliersResponse = z.infer<
  typeof listSdSuppliersResponseSchema
>;
export type GetSdSupplierResponse = z.infer<typeof getSdSupplierResponseSchema>;
